import { alpha, type Theme } from '@mui/material'
import { authPx } from './authScale'

import { fieldAccentColor, fieldErrorRing, fieldFill, fieldFocusRing } from '@repo/ui'

/**
 * The one source for the AUTH pages' field, SSO-button and OTP-box chrome.
 *
 * WHY THIS FILE EXISTS. This treatment was hand-rolled twice — `SignInPage` and
 * `AuthPasswordField` each declared its own `fieldFill` / `fieldFillActive` /
 * `fieldBorder` trio from the same tokens — and it had already DRIFTED, in the
 * way §36 predicts a second hand-roll will. Both copies made `fieldFill`
 * mode-aware and left `fieldFillActive` as a bare `alpha(text.primary, 0.12)`.
 * On dark that is a 12% white wash, which is the intended look. On LIGHT,
 * `text.primary` is #09090b, so hovering or focusing an input composited to
 * rgb(233,233,234) heading toward a dark grey — the field visibly darkened
 * under the cursor on a white page. `ssoFillHover` had the identical defect at
 * 8%, and the OTP empty-digit fill at 6%.
 *
 * The bug is not that someone wrote the wrong number. It is that the mode-aware
 * decision lived in one expression and the states that had to follow it lived in
 * three others, in two files. Extracting is what stops that recurring, so these
 * helpers are where the auth-page field chrome lives now; do not re-derive a
 * fill or a border at a call site.
 *
 * WHY NOT JUST `@repo/ui`'s `fieldChrome`. The auth pages are a deliberate
 * override of it, for a reason recorded below: their fields sit on a tinted,
 * ANIMATED page rather than on a white card, and `primary[100]` measured 1.13:1
 * against that page. So this module takes `@repo/ui`'s ACCENT and RING verbatim
 * (the §31 focus treatment, which is page-independent) and keeps the auth pages'
 * own fill and rest border.
 *
 * VRG-315: the duplicate `@mui/material` in the pnpm store means the `Theme`
 * here and the `Theme` the `@repo/ui` helpers compiled against are structurally
 * identical and nominally different. Same cast as `signupPaymentAppearance.ts`,
 * derived from the helper's own signature so it stops compiling if that
 * signature really changes.
 */
type FieldChromeTheme = Parameters<typeof fieldFill>[0]

const chrome = (theme: Theme) => theme as unknown as FieldChromeTheme

/**
 * LIGHT mode fills are OPAQUE; dark keeps the translucent wash.
 *
 * Two problems, one cause. The fields were translucent (8%), so (a) they had
 * almost no contrast on a light page — 1.21:1 measured — and (b) the orbiting
 * blob showed straight THROUGH them, which reads as the background moving in
 * front of the form. It is not: `elementFromPoint` at a field's centre returns
 * the field. A translucent control on an animated backdrop simply looks like
 * one that is behind it.
 *
 * An opaque fill fixes both at once, and is the right default anyway: a text
 * input is a surface you write on, not a tint. Dark keeps the wash because
 * there it reads correctly and is the approved look.
 */
export const authFieldFill = (theme: Theme): string =>
  theme.palette.mode === 'light'
    ? theme.palette.background.paper
    : alpha(theme.palette.text.primary, 0.08)

/**
 * Hover AND focus fill.
 *
 * DARK is unchanged: one step brighter than rest (8% -> 12%), which is that
 * mode's entire interaction signal and is the approved look.
 *
 * LIGHT MUST NOT DARKEN, and that is the whole point of this helper. The old
 * `alpha(text.primary, 0.12)` was written for a hardcoded-black shell where it
 * meant "12% lighter"; under a white shell the same expression means "12%
 * darker", and the field turned a dark grey under the cursor. There is no
 * headroom to brighten from white either, so light steps the other way by a
 * hair — to #fafafa, which is `@repo/ui`'s own `fieldFill`, a 1.02:1 move.
 *
 * A 1.02:1 step is not a usable focus indicator on its own, and pretending
 * otherwise would trade a cosmetic bug for an a11y one. That is why light also
 * takes the border + ring below: in light the BORDER carries the state change,
 * with the fill as a whisper underneath it; in dark the FILL carries it.
 */
export const authFieldFillActive = (theme: Theme): string =>
  theme.palette.mode === 'light'
    ? fieldFill(chrome(theme))
    : alpha(theme.palette.text.primary, 0.12)

/**
 * Rest border.
 *
 * VERA-1095: the auth fields are FILL-ONLY, and a fill-only field cannot work
 * in both themes. On near-black, adding 8% white has a full range of headroom
 * and reads instantly. On near-white, removing 8% has almost none: measured,
 * the fill composited to ~#e0e1e1 against a #f4f5f5 page — 1.21:1, where WCAG
 * asks 3:1 for a component boundary. The fields were not low-contrast, they
 * were invisible. Same luminance-headroom asymmetry as the ambient blobs.
 *
 * A BORDER is the fix rather than a darker fill, because a 1px edge does not
 * depend on headroom, and does not depend on what is behind it — which matters
 * here, since the backdrop is an animated gradient whose luminance changes as
 * the blobs orbit. Any fill has varying contrast against it; a border does not.
 *
 * LIGHT ONLY. Dark's fill-only treatment reads correctly and is the approved
 * look, so it keeps `none`. Deliberately NOT §31's `primary[100]`: that token is
 * calibrated for a field sitting on a WHITE card; measured against this page's
 * tinted #f4f5f5 it came out at 1.13:1 — worse than the fill it was meant to
 * rescue. A border only helps if it is darker than the surface BEHIND it, and
 * here that surface is the page, not a card.
 */
export const authFieldBorder = (theme: Theme): string =>
  theme.palette.mode === 'light'
    ? `1px solid ${alpha(theme.palette.text.primary, 0.23)}`
    : 'none'

/**
 * Hover AND focus border colour — `brand[600]`, read from `@repo/ui` rather
 * than restated, so the auth pages cannot drift from every other field in the
 * app (§31: hover and focus are the same brand colour).
 *
 * Inert in dark by construction: the rest border there is `none`, so a
 * `border-color` change has nothing to paint. That is deliberate — dark's fill
 * step already signals the state, and adding a 1px edge on hover would also
 * shift the layout by a pixel.
 */
export const authFieldAccent = (theme: Theme): string => fieldAccentColor(chrome(theme))

/**
 * The soft brand ring focus adds on top of the accent border (§31).
 *
 * LIGHT ONLY, for the same reason the border is: dark's 8% -> 12% fill step is
 * a legible focus indicator already, and the brief for this treatment is that
 * dark comes out unchanged. Light has no such step left once the fill stops
 * darkening, so without this there would be no focus indicator at all.
 */
export const authFieldFocusRing = (theme: Theme): string =>
  theme.palette.mode === 'light' ? fieldFocusRing(chrome(theme)) : 'none'

/**
 * The invalid-field edge — kept at the auth pages' own 0.7 alpha rather than
 * `@repo/ui`'s full-strength `fieldErrorColor`, so the field outline stays a
 * touch quieter than the message that explains it.
 */
export const authFieldErrorColor = (theme: Theme): string =>
  alpha(theme.palette.error.main, 0.7)

/** Focus ring for an INVALID field — same geometry, error colour (§31). */
export const authFieldErrorRing = (theme: Theme): string =>
  theme.palette.mode === 'light' ? fieldErrorRing(chrome(theme)) : 'none'

/**
 * The whole `.MuiInputBase-root` treatment for an auth-page text field.
 *
 * The tokens above were the duplicated part, but so was this block — the email
 * field and the password field declared byte-identical rule sets. Exporting the
 * assembled sx means a state can't be added to one field and forgotten on the
 * other, which is the failure this extraction exists to prevent.
 *
 * Note the selector is `.MuiOutlinedInput-notchedOutline`, not the `fieldset`
 * element the call sites used before. An outlined input draws its border on a
 * notched-outline CHILD, and MUI's own hover/focus rules for it are
 * `.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline` — three
 * classes. `& fieldset` is one class plus an element, so it LOSES to MUI on
 * hover and focus and only ever won at rest. Matching MUI's shape is what makes
 * these rules actually apply; it also lets us pin `borderWidth` to 1px, since
 * MUI's focused rule otherwise thickens the outline to 2px and shifts the text.
 *
 * Error beats accent in every state, deliberately: falling through to the brand
 * ring on focus would turn the field the app's "this is fine" colour at exactly
 * the moment the user clicked in to fix it.
 *
 * "Every state" INCLUDES rest, and that needs saying because the first version
 * of this block covered only hover and focus. MUI has its own
 * `.Mui-error .MuiOutlinedInput-notchedOutline` rule at FULL-strength
 * `error.main`, so with rest unclaimed an invalid field measured rgb(211,47,47)
 * at rest and rgba(211,47,47,0.7) the moment you hovered it — the edge got
 * QUIETER as you reached for it, which is the same backwards-interaction defect
 * this module was extracted to stop.
 */
export const authFieldRootSx = (theme: Theme) => {
  const accent = authFieldAccent(theme)
  return {
    bgcolor: authFieldFill(theme),
    // Lengths follow `--auth-scale` (authScale.ts): unset = these exact px.
    // The input's own padding is restated so the field's 56px height scales
    // with its type instead of keeping MUI's fixed 16.5px/14px around it.
    borderRadius: authPx(8),
    minHeight: authPx(48),
    color: 'text.primary',
    fontSize: authPx(16),
    '& .MuiInputBase-input': {
      padding: `${authPx(16.5)} ${authPx(14)}`,
    },
    '& .MuiInputBase-input.MuiInputBase-inputAdornedEnd': {
      paddingRight: 0,
    },
    '&.MuiInputBase-adornedEnd': {
      paddingRight: authPx(14),
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: authFieldBorder(theme),
    },
    '&:hover': {
      bgcolor: authFieldFillActive(theme),
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: accent,
    },
    '&.Mui-focused': {
      bgcolor: authFieldFillActive(theme),
      boxShadow: authFieldFocusRing(theme),
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: accent,
      borderWidth: '1px',
    },
    /* ⚠️ AN INVALID FIELD LOOKS LIKE A VALID ONE, 2026-09-24 (Spencer: "not
       highlight the whole field and have it be like the enter page"). The
       message under the field carries the error, exactly as vera.inc /enter
       does. The red edge it replaces was drawn TWICE, an `outline` outside
       the notched border, which read as a clipped double line on the glass
       card. `error` is still passed, so the input keeps `aria-invalid`; these
       rules only undo MUI's own full-strength red `.Mui-error` border, which
       would otherwise come back at rest. The note above about error beating
       accent is superseded: there is no error colour on the field to beat. */
    '&.Mui-error .MuiOutlinedInput-notchedOutline': {
      border: authFieldBorder(theme),
    },
    '&.Mui-error:hover .MuiOutlinedInput-notchedOutline, &.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline':
      {
        borderColor: accent,
        borderWidth: '1px',
      },
  }
}

/**
 * THE BRAND RAMP — the two stops every purple gradient on the signup surface is
 * drawn from, and the one place they are written down.
 *
 * WHY IT MOVED HERE. `SignupSuccessMark` declared these stops privately as
 * `IRIS_STOPS`. The step rail then needed the same ramp, which is the second
 * hand-roll §36 names as the trigger to extract — and extracting now rather than
 * later matters more than usual, because the two uses have DIFFERENT contrast
 * bars and a private copy would let them diverge without anyone noticing:
 *
 *   the mark  — a 2px SVG STROKE on the page. Graphical object, 3:1.
 *   the rail  — a FILL under a 12px bold numeral. Text, 4.5:1.
 *
 * A ramp that satisfies the stroke can fail the numeral sitting on it, so the
 * stops below are chosen against the STRICTER of the two and shared, rather than
 * each surface picking its own and calling it the brand colour.
 *
 * MODE-AWARE, and not symmetrically. Light runs dark-purple so a white numeral
 * has something to sit on; dark runs light-purple because the dark end of the
 * canonical ramp measured 1.73:1 against a #09090b page and the mark dissolved
 * into it. The fill inverts with the page, so the INK on it inverts too
 * (`brandRampInk`) — which is the opposite of the rule the CTA follows, and the
 * difference is real: the CTA's fill does not change with mode, this one does.
 *
 * MEASURED, against the ink each mode actually uses:
 *
 *   light  rgb(130,90,195) -> rgb(55,39,137)    white ink     5.00:1 .. 11.58:1
 *   dark   rgb(169,139,240) -> rgb(140,95,230)  near-black    7.24:1 ..  4.64:1
 *
 * Both ends clear 4.5:1 in both modes, which the flat `brand[500]` this replaced
 * did NOT: white on #8F68EA measures 3.94:1, so the step numerals were failing
 * the text bar before this ramp existed. That is the argument for the gradient
 * over and above how it looks.
 *
 * The dark end is rgb(140,95,230) rather than the rgb(127,72,223) the mark used
 * privately. 127,72,223 gives 3.69:1 under a near-black numeral — under the bar —
 * and it also puts the mark's own stroke at 3.69:1 against the page, which the
 * mark's comment described as "clear of the 4.5:1 bar". It was not; that claim
 * was wrong as written. Lifting the stop fixes the rail and the mark together,
 * and moves in the same direction as the mode-aware fix that preceded it.
 */
const BRAND_RAMP = {
  light: ['rgb(130, 90, 195)', 'rgb(55, 39, 137)'],
  dark: ['rgb(169, 139, 240)', 'rgb(140, 95, 230)'],
} as const

/** The ramp's two stops, light end first. */
export const brandRampStops = (theme: Theme): readonly [string, string] =>
  BRAND_RAMP[theme.palette.mode === 'dark' ? 'dark' : 'light']

/**
 * The ramp as a CSS gradient. 135deg by default — top-left to bottom-right, so a
 * round fill reads as lit from the same direction as everything else on the page.
 */
export const brandRampGradient = (theme: Theme, angle = '135deg'): string => {
  const [from, to] = brandRampStops(theme)
  return `linear-gradient(${angle}, ${from}, ${to})`
}

/**
 * The ink that goes ON the ramp — white in light, the page's near-black in dark,
 * because the ramp itself inverts.
 *
 * `background.default` rather than a literal, so it tracks the theme's own
 * near-black instead of introducing a third absolute (same reasoning as the CTA's
 * label).
 */
export const brandRampInk = (theme: Theme): string =>
  theme.palette.mode === 'dark'
    ? theme.palette.background.default
    : theme.palette.common.white

/**
 * Resting SSO-button surface.
 *
 * Same reasoning as `authFieldFill`: opaque in light so the blob cannot pass
 * through a control that sits in front of it. The weight is a hierarchy, not an
 * arbitrary number — an SSO button is a resting SURFACE (4%, barely there)
 * where a field is a RECESS the user is asked to type into (8%).
 */
export const authSsoFill = (theme: Theme): string =>
  theme.palette.mode === 'light'
    ? theme.palette.background.paper
    : alpha(theme.palette.text.primary, 0.04)

/** Hover surface — dark steps 4% -> 8%; light steps to #fafafa, never darker. */
export const authSsoFillHover = (theme: Theme): string =>
  theme.palette.mode === 'light'
    ? fieldFill(chrome(theme))
    : alpha(theme.palette.text.primary, 0.08)

/**
 * The whole SSO-button treatment — Google, Microsoft, Apple declared this block
 * three times over.
 *
 * The border matches the fields', in the same mode and for the same reason:
 * these are fill-only too — 4% on a light page is even fainter than the fields'
 * 8% — and they sit directly under them, so bordering one and not the other
 * would read as two different kinds of control in one column. It follows that
 * the hover/focus ACCENT has to match as well, or the column would change state
 * in two different visual languages.
 */
export const authSsoButtonSx = (theme: Theme) => {
  const accent = authFieldAccent(theme)

  return {
    height: authPx(48),
    minHeight: authPx(40),
    borderRadius: authPx(8),
    border: authFieldBorder(theme),
    bgcolor: authSsoFill(theme),
    color: 'text.primary',
    fontSize: authPx(16),
    // The provider mark and its gap, which MUI sizes in fixed px.
    '& .MuiButton-startIcon': { mr: authPx(8), ml: authPx(-4) },
    '& .MuiButton-startIcon > *:nth-of-type(1)': { fontSize: authPx(20) },
    fontWeight: 400,
    textTransform: 'none' as const,
    '&:hover': {
      bgcolor: authSsoFillHover(theme),
      borderColor: accent,
    },
    '&.Mui-focusVisible': {
      borderColor: accent,
      boxShadow: authFieldFocusRing(theme),
    },
  }
}

/**
 * The fill of an OTP digit box — ONE value, in every state.
 *
 * A box carries the neutral wash: one step quieter than a text field (6% vs 8%
 * in dark), because there are six of them in a row and at field weight they read
 * as a solid bar. Light inverts to #fafafa for the same reason as every other
 * fill in this module; at 6% of #09090b it was a grey wash on a white page.
 *
 * THE FILL NO LONGER MOVES, and that is the point of this rewrite rather than an
 * incidental simplification. It used to change on three separate axes — empty vs
 * filled, hover, and focus — each written independently, and in light every one
 * of them went the WRONG way:
 *
 *   focused  -> `transparent`, i.e. the #f4f5f5 PAGE, DARKER than the #fafafa it
 *               replaced. This is the "grey bg tint on focus" as reported.
 *   filled   -> `transparent` too, so an answered box read greyer than an
 *               unanswered one.
 *   hover    -> `authFieldFill` (white), i.e. LIGHTER, the opposite direction
 *               from the text fields' own hover one component over.
 *
 * Three fill transitions on one control cannot all be right, and picking better
 * values for each would leave the next person the same three decisions. So the
 * border carries every state and the fill carries none: neutral edge at rest,
 * `brand[600]` once the box is answered or focused, plus light's soft ring. That
 * is the user's ask — "no grey bg tint on focus, just the purple border" —
 * arrived at as a rule rather than a patch.
 *
 * Costs it, honestly: dark loses its empty-vs-filled fill distinction, which was
 * part of that mode's fill-first language. Answered is still marked by the brand
 * border, by `text.primary` over `text.secondary`, and by there being a digit in
 * the box.
 */
export const authOtpFill = (theme: Theme): string =>
  theme.palette.mode === 'light'
    ? fieldFill(chrome(theme))
    : alpha(theme.palette.text.primary, 0.06)

/**
 * The whole `.MuiInputBase-root` treatment for one OTP digit box.
 *
 * SIZING IS DERIVED, NOT DECLARED. The box was a hardcoded 48px square, so six
 * of them plus their gaps came to 308px inside a 396px column — the row stopped
 * 88px short of the Submit button directly beneath it, and the block did not
 * read as one column. `width: 100%` inside a `flex: 1 1 0` cell means the row
 * fills whatever the column is, and `aspect-ratio: 1` keeps each cell square as
 * it grows, so a digit box never stretches into a letterbox that stops reading
 * as a digit cell. It is also count-agnostic: the same rule sizes four boxes or
 * six without a second magic number to keep in sync with the array length.
 *
 * Everything is drawn on `.MuiOutlinedInput-notchedOutline`, and the ROOT no
 * longer carries a border of its own. It used to carry both — a brand ring on
 * the root and a neutral one on the `fieldset` — which is two concentric edges
 * for one idea, and the `& fieldset` half lost to MUI's own three-class rules
 * anyway: measured, hovering a box painted `rgba(0,0,0,0.87)` (near-black) and
 * focusing it painted `rgb(109,109,109)` at 2px. Both are exactly the "dark grey
 * on a light page" defect this module exists to stop, still live because that
 * selector never actually applied.
 *
 * HOVER IS DELIBERATELY INERT. Six boxes are one control — you click anywhere
 * and type through them in order — so lighting up whichever one the cursor
 * happens to cross would advertise six targets, and it would use the same brand
 * edge that means "answered" two states over. Hover is pinned to the rest border
 * rather than left unset, because unset is not neutral here: MUI's default wins
 * the moment you take your hands off it.
 */
/**
 * The rest edge of an EMPTY OTP box — and the one place this module departs from
 * `authFieldBorder`, deliberately.
 *
 * LIGHT is `authFieldBorder` unchanged: rgba(0,0,0,0.23), measured #c1c1c1
 * against the box fill, which reads fine and is the same edge the text fields
 * carry.
 *
 * DARK gets a border where a text FIELD gets none, and the difference is not
 * inconsistency — it is arithmetic about how many targets there are. Dark's
 * fields are fill-only, and a lone field at 1.12:1 against the page is findable
 * because there is exactly one of it and a caret sitting in it. Six squares at
 * that same 1.12:1 are not: the user is being asked to locate a ROW of empty
 * slots and count their position in it, which the fill does not support at that
 * contrast. Same fill, different job, so the edge earns its place here and not
 * one component over.
 *
 * `primary[400]` — the SAME token the shared Switch's off-track boundary takes,
 * resolving to #71717a in dark for 4.12:1 against the page. Reusing it rather
 * than picking a new grey means the two "here is an empty control" edges in this
 * release are one value, already proven to clear 3:1 on both dark surfaces.
 *
 * REST ONLY. Hover stays inert and filled/focused still take the brand edge, so
 * this makes an empty box findable without competing with the focused one.
 */
export const authOtpRestBorder = (theme: Theme): string =>
  theme.palette.mode === 'light'
    ? authFieldBorder(theme)
    : // Indexed directly rather than through a cast: the platform's palette
      // augmentation types the numeric ramp, which is how `TagChip` and
      // `SearchSelect` already read `primary[100]`/`[200]`. The `??` is for the
      // ramp key being optional in that augmentation, not for it being absent.
      `1px solid ${theme.palette.primary[400] ?? '#888888'}`

export const authOtpBoxSx = (theme: Theme, filled: boolean) => {
  const accent = authFieldAccent(theme)
  const restEdge = authOtpRestBorder(theme)
  const activeEdge = `1px solid ${accent}`

  return {
    width: '100%',
    aspectRatio: '1 / 1',
    bgcolor: authOtpFill(theme),
    borderRadius: '12px',
    color: filled ? 'text.primary' : 'text.secondary',
    '& .MuiOutlinedInput-notchedOutline': {
      border: filled ? activeEdge : restEdge,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      border: filled ? activeEdge : restEdge,
    },
    '&.Mui-focused': {
      boxShadow: authFieldFocusRing(theme),
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      border: activeEdge,
      // MUI thickens a focused outline to 2px, which on a box this size is a
      // visible weight change on top of the colour change. One idea, one edge.
      borderWidth: '1px',
    },
  }
}
